import { useEffect, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { previewCron, type PreviewResult } from '../../api/cron';

// ── Dropdown option sets ───────────────────────────────────────────────────

const MINUTE_OPTS = [
  { value: '*',   label: 'Every minute' },
  { value: '*/2', label: 'Every 2 minutes' },
  { value: '*/5', label: 'Every 5 minutes' },
  { value: '*/10',label: 'Every 10 minutes' },
  { value: '*/15',label: 'Every 15 minutes' },
  { value: '*/30',label: 'Every 30 minutes' },
  { value: '0',   label: 'At :00' },
  { value: '15',  label: 'At :15' },
  { value: '30',  label: 'At :30' },
  { value: '45',  label: 'At :45' },
];

const HOUR_OPTS = [
  { value: '*',   label: 'Every hour' },
  { value: '*/2', label: 'Every 2 hours' },
  { value: '*/4', label: 'Every 4 hours' },
  { value: '*/6', label: 'Every 6 hours' },
  { value: '*/12',label: 'Every 12 hours' },
  { value: '0',   label: 'Midnight (00:00)' },
  { value: '6',   label: 'Morning (06:00)' },
  { value: '8',   label: 'Business start (08:00)' },
  { value: '12',  label: 'Noon (12:00)' },
  { value: '18',  label: 'Evening (18:00)' },
  { value: '20',  label: 'Night (20:00)' },
  { value: '23',  label: 'Late night (23:00)' },
];

const DOM_OPTS = [
  { value: '*',  label: 'Every day' },
  { value: '1',  label: '1st of month' },
  { value: '15', label: '15th of month' },
];

const MONTH_OPTS = [
  { value: '*',  label: 'Every month' },
  { value: '1',  label: 'January' },
  { value: '2',  label: 'February' },
  { value: '3',  label: 'March' },
  { value: '4',  label: 'April' },
  { value: '5',  label: 'May' },
  { value: '6',  label: 'June' },
  { value: '7',  label: 'July' },
  { value: '8',  label: 'August' },
  { value: '9',  label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const DOW_OPTS = [
  { value: '*',   label: 'Every day of week' },
  { value: '1-5', label: 'Weekdays (Mon–Fri)' },
  { value: '6,0', label: 'Weekends (Sat & Sun)' },
  { value: '1',   label: 'Monday' },
  { value: '2',   label: 'Tuesday' },
  { value: '3',   label: 'Wednesday' },
  { value: '4',   label: 'Thursday' },
  { value: '5',   label: 'Friday' },
  { value: '6',   label: 'Saturday' },
  { value: '0',   label: 'Sunday' },
];

// ── Human summary ──────────────────────────────────────────────────────────

function buildSummary(min: string, hr: string, dom: string, mon: string, dow: string): string {
  const parts: string[] = [];

  // Frequency
  if (min === '*') parts.push('every minute');
  else if (min.startsWith('*/')) parts.push(`every ${min.slice(2)} minutes`);
  else parts.push(`at minute :${min.padStart(2, '0')}`);

  // Hour qualifier
  if (hr !== '*') {
    if (hr.startsWith('*/')) parts.push(`every ${hr.slice(2)} hours`);
    else parts.push(`at ${hr.padStart(2, '0')}:00`);
  }

  // Day of week
  if (dow === '1-5') parts.push('weekdays only');
  else if (dow === '6,0') parts.push('weekends only');
  else if (dow !== '*') {
    const d = DOW_OPTS.find((o) => o.value === dow);
    if (d) parts.push(`on ${d.label.toLowerCase()}`);
  }

  // Month
  if (mon !== '*') {
    const m = MONTH_OPTS.find((o) => o.value === mon);
    if (m) parts.push(`in ${m.label.toLowerCase()}`);
  }

  // Day of month
  if (dom !== '*') parts.push(`on the ${dom}${dom === '1' ? 'st' : dom === '15' ? 'th' : 'th'} of the month`);

  return parts.length ? parts.join(', ') : 'every minute';
}

// ── Component ──────────────────────────────────────────────────────────────

interface Props {
  value: string;
  onChange: (expr: string) => void;
}

const CUSTOM = '__custom__';

function findOrCustom(opts: { value: string }[], val: string) {
  return opts.some((o) => o.value === val) ? val : CUSTOM;
}

export function CronIntervalBuilder({ value, onChange }: Props) {
  const parts = value.split(' ');
  const [min, setMin] = useState(parts[0] ?? '*');
  const [hr,  setHr]  = useState(parts[1] ?? '*');
  const [dom, setDom] = useState(parts[2] ?? '*');
  const [mon, setMon] = useState(parts[3] ?? '*');
  const [dow, setDow] = useState(parts[4] ?? '*');
  const [rawExpr, setRawExpr] = useState(value);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Rebuild expression when dropdowns change
  useEffect(() => {
    const expr = `${min} ${hr} ${dom} ${mon} ${dow}`;
    setRawExpr(expr);
    onChange(expr);
  }, [min, hr, dom, mon, dow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch preview whenever rawExpr changes (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const result = await previewCron(rawExpr, 5);
        if (result.success) {
          setPreview(result);
          setPreviewError('');
        } else {
          setPreview(null);
          setPreviewError(result.error);
        }
      } catch {
        setPreviewError('Failed to fetch preview');
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
  }, [rawExpr]);

  function handleRawChange(expr: string) {
    setRawExpr(expr);
    onChange(expr);
    const p = expr.trim().split(/\s+/);
    if (p.length === 5) {
      setMin(p[0]); setHr(p[1]); setDom(p[2]); setMon(p[3]); setDow(p[4]);
    }
  }

  function handleCopy() {
    void navigator.clipboard.writeText(rawExpr);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const summary = buildSummary(min, hr, dom, mon, dow);

  return (
    <Box>
      {/* Dropdowns */}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {[
          { label: 'Minute',       opts: MINUTE_OPTS, val: min, set: setMin },
          { label: 'Hour',         opts: HOUR_OPTS,   val: hr,  set: setHr  },
          { label: 'Day of month', opts: DOM_OPTS,    val: dom, set: setDom },
          { label: 'Month',        opts: MONTH_OPTS,  val: mon, set: setMon },
          { label: 'Day of week',  opts: DOW_OPTS,    val: dow, set: setDow },
        ].map(({ label, opts, val, set }) => (
          <FormControl key={label} size="small" sx={{ minWidth: 155 }}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={findOrCustom(opts, val)}
              label={label}
              onChange={(e) => {
                if (e.target.value !== CUSTOM) set(e.target.value);
              }}
            >
              {opts.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
              {findOrCustom(opts, val) === CUSTOM && (
                <MenuItem value={CUSTOM} disabled sx={{ fontStyle: 'italic', fontSize: '0.8rem' }}>Custom: {val}</MenuItem>
              )}
            </Select>
          </FormControl>
        ))}
      </Stack>

      {/* Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Summary:</strong> {summary}
      </Typography>

      {/* Raw expression override */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          label="Expression"
          value={rawExpr}
          onChange={(e) => handleRawChange(e.target.value)}
          inputProps={{ style: { fontFamily: 'monospace' } }}
          sx={{ width: 180 }}
        />
        <Tooltip title={copied ? 'Copied!' : 'Copy'}>
          <Button size="small" variant="outlined" onClick={handleCopy} sx={{ minWidth: 0, px: 1 }}>
            <ContentCopyIcon fontSize="small" />
          </Button>
        </Tooltip>
      </Stack>

      {/* Next-run preview */}
      <Box
        sx={{
          bgcolor: 'action.hover',
          borderRadius: 1,
          px: 1.5,
          py: 1,
          minHeight: 90,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          ⏱ Next 5 scheduled runs
        </Typography>
        {previewLoading && <CircularProgress size={14} />}
        {previewError && <Alert severity="error" sx={{ py: 0 }}>{previewError}</Alert>}
        {!previewLoading && !previewError && preview?.runs.map((r, i) => (
          <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
            → {new Date(r).toLocaleString('de-DE', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
